// Scenarios for controls the application performs itself. Their operating effectiveness rests on
// the IT general controls that keep the configuration unchanged and access controlled, so this is
// where a condition pointing at another control does its work.
//
// Titles and taglines track the design document so a walkthrough can follow it section by section.
import {Conclusion, DemoControl, Scenario} from "../model";
import {
  LEGACY,
  SAP,
  batchJobs,
  CAST_EDGES,
  changeManagement,
  creditLimit,
  customerMasterReview,
  duplicateInvoice,
  effective,
  goodsReceiptReview,
  logicalAccess,
  logicalAccessFailedAtSap,
  payrollChangeApproval,
  payrollReconciliation,
  privilegedAccess,
  revenueCutOff,
  threeWayMatch,
} from "./cast";

const FY26 = {from: "2026-01-01", to: "2026-12-31"};

const unconcluded: Conclusion = {outcome: "insufficient_evidence", supportsReliance: false, justification: ""};

const creditLimitConclusion = effective(
  "The application rejects an order taking a customer above its approved credit limit. Relied upon on " +
    "the basis of the SAP ITGCs.",
);

/** The controls on stage for every automated scenario, with the focused control's own verdict swapped in. */
const cast = (focus: DemoControl, access: DemoControl = logicalAccess): DemoControl[] => {
  const processControls = [
    threeWayMatch(itacConclusion),
    duplicateInvoice,
    goodsReceiptReview,
    creditLimit(creditLimitConclusion),
    customerMasterReview,
    revenueCutOff,
    payrollChangeApproval,
    payrollReconciliation,
  ];
  return [
    privilegedAccess,
    changeManagement,
    access,
    batchJobs,
    ...processControls.map(c => (c.ref === focus.ref ? focus : c)),
  ];
};

const itacConclusion = effective(
  "The control is automated, so its operating effectiveness for the period rests on its design and " +
    "implementation — including a test confirming the application enforces an approver distinct from " +
    "the originator before posting — together with reliance on the change-management and " +
    "logical-access ITGCs that keep the configuration unchanged and access controlled. Both ITGCs " +
    "were concluded effective, so the automated control is taken to have operated throughout the " +
    "period. No transaction sample was required.",
);

const itacBasis = {
  id: "b1",
  status: "obtained" as const,
  span: FY26,
  evidence: {
    kind: "test" as const,
    label: "VI-02 configuration re-performance — 15 May 2026",
    itemsTested: 1,
    exceptions: 0,
    outcome: "operated_effectively" as const,
    conclusion:
      "The posting screen was observed to reject a transaction where the approver matched the " +
      "originator, and to accept the same transaction once a second approver was applied.",
  },
  conditions: [
    {kind: "control" as const, control: "ITGC-02", scope: SAP},
    {kind: "control" as const, control: "ITGC-03", scope: SAP},
  ],
};

export const automatedControl: Scenario = {
  id: "s2",
  section: "Scenario 2",
  title: "Automated three-way match relying on ITGCs",
  tagline: "the automated-control case",
  situation:
    "VI-02, the three-way match, automated in SAP S/4HANA: the system blocks posting where approver = originator. " +
    "Scoped per application. We observed the configuration once, on 15 May 2026, and re-performed it " +
    "with two test transactions.",
  focus: {control: "VI-02", scope: "Global"},
  controls: cast(threeWayMatch(itacConclusion)),
  edges: CAST_EDGES,
  oe: [{control: "VI-02", scope: "Global", period: FY26, basis: [itacBasis], conclusion: itacConclusion}],
};

export const automatedControlFailed: Scenario = {
  ...automatedControl,
  id: "s2-failed",
  section: "Scenario 2 · stress",
  title: "…after ITGC-03 is concluded not effective",
  tagline: "coverage drops to zero, not to nine months",
  situation:
    "A leavers' access review failure in Q4, at SAP only. Without the ITGCs, a single May observation " +
    "says nothing about December — so the whole period is uncovered, not just the quarter that failed.",
  controls: cast(threeWayMatch(unconcluded), logicalAccessFailedAtSap),
  oe: [{control: "VI-02", scope: "Global", period: FY26, basis: [itacBasis], conclusion: unconcluded}],
};

const benchmarkConclusion = effective(
  "O2C-01 was tested in full in FY25 and concluded effective. The SAP change log shows no " +
    "modification to the credit-limit configuration since, the control is not associated with a " +
    "significant risk, and both supporting ITGCs were concluded effective for FY26. Benchmarking is " +
    "permitted under firm policy on that basis and the control is taken to have operated throughout " +
    "the period.",
);

const benchmarkBasis = (noChangeMet: boolean) => ({
  id: "b1",
  status: "obtained" as const,
  span: FY26,
  evidence: {
    kind: "prior_conclusion" as const,
    control: "O2C-01",
    scope: "Global",
    period: "FY25",
    outcome: "operated_effectively" as const,
    justification:
      "A full population test of 8,412 orders in FY25 found no order accepted above its customer's " +
      "credit limit. The control was concluded to have operated effectively throughout FY25.",
  },
  conditions: [
    {
      kind: "note" as const,
      text: "No change to the control configuration per SAP change log",
      met: noChangeMet,
      assertedBy: "A. Okafor",
      assertedAt: "14 Jan 2027",
    },
    {
      kind: "note" as const,
      text: "Not a significant risk; benchmarking permitted per firm policy",
      met: true,
      assertedBy: "R. Mehta",
      assertedAt: "14 Jan 2027",
    },
    {kind: "control" as const, control: "ITGC-02", scope: SAP},
    {kind: "control" as const, control: "ITGC-03", scope: SAP},
  ],
});

export const benchmarking: Scenario = {
  id: "s5",
  section: "Scenario 5",
  title: "Prior-period benchmarking of an automated control",
  tagline: "prior conclusion as evidence",
  situation:
    "O2C-01, an automated credit-limit check in SAP. Tested in full in FY25 and concluded effective. " +
    "Under the firm's benchmarking policy, an automated control need not be retested annually provided " +
    "nothing changed and the ITGCs remain effective.",
  focus: {control: "O2C-01", scope: "Global"},
  controls: cast(creditLimit(benchmarkConclusion)),
  edges: CAST_EDGES,
  oe: [
    {control: "O2C-01", scope: "Global", period: FY26, basis: [benchmarkBasis(true)], conclusion: benchmarkConclusion},
  ],
};

export const benchmarkingChanged: Scenario = {
  ...benchmarking,
  id: "s5-changed",
  section: "Scenario 5 · stress",
  title: "…but the change log shows a modification in March",
  tagline: "the attestation the auditor can no longer make",
  situation:
    "The credit-limit logic was modified in March. The 'no change' statement can't be made, so the " +
    "prior conclusion carries nothing and the model forces the question before a benchmarking " +
    "conclusion gets written over a changed control.",
  controls: cast(creditLimit(unconcluded)),
  oe: [{control: "O2C-01", scope: "Global", period: FY26, basis: [benchmarkBasis(false)], conclusion: unconcluded}],
};

const legacyConclusion = effective(
  "The legacy three-way match was tested over the six months to migration, with the configuration " +
    "re-performed and the supporting ITGCs concluded effective for that period.",
);

const sapPostMigration = effective(
  "Following migration on 1 July the control was re-performed in SAP, with the SAP ITGCs concluded " +
    "effective for the remainder of the period.",
);

export const migration: Scenario = {
  id: "s7",
  section: "Scenario 7",
  title: "Mid-period system migration",
  tagline: "the case that usually forces a special case",
  situation:
    "The entity migrated from a legacy ledger to SAP on 1 July 2026. The three-way-match control " +
    "exists in both, so each scope carries its own shortened period.",
  focus: {control: "VI-02", scope: SAP},
  controls: cast({
    ref: "VI-02",
    title: "Three-way match",
    assessment: "Vendor Invoice",
    automated: true,
    scopes: [
      {scope: LEGACY, design: "effective", implementation: "effective", oe: legacyConclusion},
      {scope: SAP, design: "effective", implementation: "effective", oe: sapPostMigration},
    ],
  }),
  edges: CAST_EDGES,
  oe: [
    {
      control: "VI-02",
      scope: LEGACY,
      period: {from: "2026-01-01", to: "2026-06-30"},
      basis: [
        {
          id: "b1",
          status: "obtained" as const,
          span: {from: "2026-01-01", to: "2026-06-30"},
          evidence: {
            kind: "test",
            label: "Legacy three-way match re-performance — 12 Feb 2026",
            itemsTested: 1,
            exceptions: 0,
            outcome: "operated_effectively",
            conclusion: "The legacy posting screen rejected a transaction where the approver matched the originator.",
          },
          conditions: [
            {
              kind: "note",
              text: "Legacy ITGCs concluded effective to migration",
              met: true,
              assertedBy: "M. Lindqvist",
              assertedAt: "09 Jul 2026",
            },
          ],
        },
      ],
      conclusion: legacyConclusion,
    },
    {
      control: "VI-02",
      scope: SAP,
      period: {from: "2026-07-01", to: "2026-12-31"},
      basis: [
        {
          id: "b1",
          status: "obtained" as const,
          span: {from: "2026-07-01", to: "2026-12-31"},
          evidence: {
            kind: "test",
            label: "VI-02 configuration re-performance — 20 Aug 2026",
            itemsTested: 1,
            exceptions: 0,
            outcome: "operated_effectively",
            conclusion: "Re-performed in SAP after cutover, with the same approver/originator rejection observed.",
          },
          conditions: [
            {kind: "control", control: "ITGC-02", scope: SAP},
            {kind: "control", control: "ITGC-03", scope: SAP},
          ],
        },
      ],
      conclusion: sapPostMigration,
    },
  ],
};
